import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, AlertTriangle, X, ArrowRight } from "lucide-react";
import { useSubscription } from "@/context/SubscriptionContext";

export default function TrialBanner() {
  const { sub } = useSubscription();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  if (!sub?.trial_active || !sub?.trial_ends_at) return null;

  const endsAt = new Date(sub.trial_ends_at);
  const msLeft = endsAt.getTime() - Date.now();
  const daysLeft = Math.max(0, Math.ceil(msLeft / 86400000));
  const sessionKey = `trial-banner-dismissed-${sub.trial_ends_at}`;
  if (dismissed || (typeof sessionStorage !== "undefined" && sessionStorage.getItem(sessionKey))) return null;

  const urgent = daysLeft <= 3;
  const planName = sub.trial_plan_name || sub.plan_name || "paid";
  const dayLabel = daysLeft === 1 ? "1 day left" : `${daysLeft} days left`;

  const close = () => {
    try { sessionStorage.setItem(sessionKey, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  const styles = urgent
    ? { bg: "rgba(244,63,94,0.12)", border: "rgba(244,63,94,0.4)", text: "#fda4af", icon: "#fb7185" }
    : { bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.35)", text: "#fcd34d", icon: "#fbbf24" };

  return (
    <div data-testid="trial-banner"
      className="mb-5 rounded-xl border px-4 py-3 flex items-center gap-3 flex-wrap"
      style={{ background: styles.bg, borderColor: styles.border }}>
      {urgent
        ? <AlertTriangle className="h-5 w-5 shrink-0" style={{ color: styles.icon }} />
        : <Clock className="h-5 w-5 shrink-0" style={{ color: styles.icon }} />}
      <div className="flex-1 min-w-[180px]">
        <div className="text-sm font-semibold" style={{ color: styles.text }} data-testid="trial-banner-text">
          {dayLabel} on your {planName} trial
        </div>
        <div className="text-xs text-slate-400 mt-0.5">
          {urgent
            ? "Your trial ends soon — upgrade now to keep full access."
            : "Enjoy full access while your trial lasts. Upgrade anytime to keep it going."}
        </div>
      </div>
      <button data-testid="trial-banner-upgrade-button" onClick={() => navigate("/billing")}
        className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold text-slate-900 transition-colors"
        style={{ background: styles.icon }}>
        Upgrade <ArrowRight className="h-4 w-4" />
      </button>
      <button data-testid="trial-banner-dismiss-button" onClick={close}
        className="h-8 w-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-white/5 transition-colors"
        title="Dismiss">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
